/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.20.0
 * @since 1.17.0
 */

import { createElement } from 'react';

import omit from 'lodash/omit';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { EReportScheduleField } from '../../components/common/ReportSchedule/types';
import { loadTableMeta as loadTableMetaAction } from '../../components/Table/actions/tableActions';
import {
    makeSelectorTableData,
    makeSelectorTableRemovedRowIndexes,
} from '../../components/Table/selectors/tableSelectors';
import { TableNameType } from '../../components/Table/types/tableTypes';
import {
    API_AUTO_REPORT_ID_ACCOUNT_TABLE_EDIT_SAVE,
    API_AUTO_REPORT_ID_ACCOUNT_TABLE_SAVE,
    API_AUTO_REPORT_ID_FORMAT_META,
    API_AUTO_REPORT_ID_FORMAT_UPDATE,
    API_AUTO_REPORT_ID_META,
    API_AUTO_REPORT_ID_PARAM,
    API_AUTO_REPORT_ID_SCHEDULE_META,
    API_AUTO_REPORT_ID_SCHEDULE_UPDATE,
    API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_EDIT_SAVE,
    API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_SAVE,
} from '../../modules/app/Api';
import { appHistoryPush } from '../../modules/app/appHistory';
import {
    makeSelectPageAutoReportIdTranslation,
    makeSelectTranslationCommon,
} from '../../modules/internationalization/intSelectors';
import { ICommonTranslation, IPageAutoReportIdTranslation } from '../../modules/internationalization/types';
import ModalErrorCommon from '../../modules/modal/components/ModalError';
import ModalSuccess from '../../modules/modal/components/ModalSuccess';
import { modalOpen } from '../../modules/modal/modalActions';
import { IModalRenderProps } from '../../modules/modal/types/modalTypes';
import { openRoute } from '../../routes/constants/externalRoutes';
import { RoutePath } from '../../routes/constants/routesConstants';
import { requestSaga } from '../../sagas/requestSaga';
import { stringFormatParams } from '../../utils/stringFormat';
import {
    autoReportIdGetMetaFailureAction,
    autoReportIdGetMetaSuccessAction,
    autoReportIdGetTabMetaFailureAction,
    autoReportIdGetTabMetaStartAction,
    autoReportIdGetTabMetaSuccessAction,
    autoReportIdLoadFormatMetaAction,
    autoReportIdLoadScheduleMetaAction,
} from './actions';
import {
    AUTOREPORT_ID_GET_META_START,
    AUTOREPORT_ID_GET_TAB_META_START,
    AUTOREPORT_ID_SAVE_TABLE,
    AUTOREPORT_ID_UPDATE_FORMAT,
    AUTOREPORT_ID_UPDATE_SCHEDULE,
} from './constants';
import { IAutoReportIdMetaResponse, mapAutoReportIdFormatDataToRequest, mapAutoReportIdMeta } from './mapping';
import { makeSelectorAutoReportCurrentId } from './selectors';
import {
    EPageAutoReportIdInfo,
    IAutoReportIdFormatMeta,
    IAutoReportIdScheduleMeta,
    TAutoReportIdGetTabMetaStartAction,
    TAutoReportIdSaveTableAction,
    TAutoReportIdUpdateFormatAction,
    TAutoReportIdUpdateScheduleAction,
} from './types';
import { getPageAutoReportIdTableUrl } from './utils/getPageAutoReportIdUrl';


function* autoReportGetApiUrl(url: string): SagaIterator {
    const id = yield select(makeSelectorAutoReportCurrentId());
    return stringFormatParams(url, { [API_AUTO_REPORT_ID_PARAM]: id });
}

function* autoReportIdShowModalErrorSaga(message?: string): SagaIterator {
    const translation: ICommonTranslation = yield select(makeSelectTranslationCommon());
    yield put(modalOpen({
        render: (props: IModalRenderProps) => createElement(ModalErrorCommon, {
            onCloseModal: props.onCloseModal,
            translation: translation.modal,
            title: message,
        }),
    }));
}

/*
* -------------------------------------------
*          AUTOREPORT ID META
* -------------------------------------------
*/

function* autoReportIdGetMetaSaga(): SagaIterator {
    try {
        const url = yield call(autoReportGetApiUrl, API_AUTO_REPORT_ID_META);
        const response: IAutoReportIdMetaResponse = yield call(requestSaga, url, 'GET');

        yield put(autoReportIdGetMetaSuccessAction(mapAutoReportIdMeta(response)));
    } catch (e: any) {
        yield put(autoReportIdGetMetaFailureAction(e));
    }
}

function* autoReportIdGetTabMetaSaga({ payload }: TAutoReportIdGetTabMetaStartAction): SagaIterator {
    try {
        switch (payload.tabId) {
            case EPageAutoReportIdInfo.FormatDelivery:
                yield call(autoReportIdGetFormatMetaSaga);
                break;
            case EPageAutoReportIdInfo.ScheduleParameters:
                yield call(autoReportIdLoadScheduleMetaSaga);
                break;
        }

        yield put(autoReportIdGetTabMetaSuccessAction());
    } catch (e) {
        yield put(autoReportIdGetTabMetaFailureAction());
    }
}

/*
* -------------------------------------------
*          AUTOREPORT ID FORMAT META
* -------------------------------------------
*/

function* autoReportIdGetFormatMetaSaga(): SagaIterator {
    const url = yield call(autoReportGetApiUrl, API_AUTO_REPORT_ID_FORMAT_META);
    const response: IAutoReportIdFormatMeta = yield call(requestSaga, url, 'GET');

    yield put(autoReportIdLoadFormatMetaAction(response));
}

function* autoReportIdSaveFormatSaga({ payload }: TAutoReportIdUpdateFormatAction): SagaIterator {
    const translation: ICommonTranslation = yield select(makeSelectTranslationCommon());
    const translationAutoReportId: IPageAutoReportIdTranslation = yield select(makeSelectPageAutoReportIdTranslation());

    try {
        const url = yield call(autoReportGetApiUrl, API_AUTO_REPORT_ID_FORMAT_UPDATE);

        yield call(requestSaga, url, 'POST', mapAutoReportIdFormatDataToRequest(payload));

        yield put(modalOpen({
            onCloseCallback: () => {
                appHistoryPush(RoutePath.settingsAutoReports);
            },
            render: (props: IModalRenderProps) => createElement(ModalSuccess, {
                onCloseModal: props.onCloseModal,
                translation: translation.modal,
                title: translationAutoReportId.formatDelivery.save.success,
            }),
        }));

        yield put(autoReportIdGetTabMetaStartAction({ tabId: EPageAutoReportIdInfo.FormatDelivery }));
    } catch (e) {
        yield call(autoReportIdShowModalErrorSaga, translationAutoReportId.formatDelivery.save.error);
    }
}

/*
* -------------------------------------------
*    AUTOREPORT ID SCHEDULE PARAMETERS
* -------------------------------------------
*/

function* autoReportIdLoadScheduleMetaSaga(): SagaIterator {
    const url = yield call(autoReportGetApiUrl, API_AUTO_REPORT_ID_SCHEDULE_META);
    const response: IAutoReportIdScheduleMeta = yield call(requestSaga, url, 'GET');

    const week = response[EReportScheduleField.Week];
    yield put(autoReportIdLoadScheduleMetaAction({
        ...response,
        [EReportScheduleField.Week]: week ? { value: week.value - 1 } : undefined,
    }));
}

function* autoReportIdUpdateScheduleSaga({ payload }: TAutoReportIdUpdateScheduleAction): SagaIterator {
    try {
        const url = yield call(autoReportGetApiUrl, API_AUTO_REPORT_ID_SCHEDULE_UPDATE);

        const period = omit(payload[EReportScheduleField.Period], 'label');
        const timeHour = omit(payload[EReportScheduleField.TimeHour], 'label');
        const timeMinute = omit(payload[EReportScheduleField.TimeMinute], 'label');
        const week = omit(payload[EReportScheduleField.Week], 'label');

        yield call(requestSaga, url, 'POST', {
            [EReportScheduleField.Description]: payload[EReportScheduleField.Description],
            [EReportScheduleField.Month]: payload[EReportScheduleField.Month],
            [EReportScheduleField.Period]: period.value ? { value: period.value } : undefined,
            [EReportScheduleField.TimeHour]: timeHour.value ? { value: timeHour.value } : undefined,
            [EReportScheduleField.TimeMinute]: timeMinute.value ? { value: timeMinute.value } : undefined,
            [EReportScheduleField.Week]: week.value ? { value: +week.value + 1 } : undefined,
        });

        yield put(autoReportIdGetTabMetaStartAction({ tabId: EPageAutoReportIdInfo.ScheduleParameters }));
    } catch (e) {
        yield call(autoReportIdShowModalErrorSaga);
    }
}

/*
* -------------------------------------------
*    AUTOREPORT ID TABLE
* -------------------------------------------
*/

function* autoReportIdSaveTableSaga({ payload }: TAutoReportIdSaveTableAction): SagaIterator {
    let url;
    const tableName = payload.tableName;

    try {
        switch (tableName) {
            case TableNameType.autoReportIdAccount:
            case TableNameType.autoReportIdSubscriber:
                const removedRowIndexes = yield select(makeSelectorTableRemovedRowIndexes(tableName));
                const data: Array<{ id: string }> = yield select(makeSelectorTableData(tableName));
                const removeIds: string[] = [];

                removedRowIndexes.forEach((rowIndex: number) => {
                    removeIds.push(data[rowIndex].id);
                });

                url = yield call(autoReportGetApiUrl, tableName === TableNameType.autoReportIdAccount
                    ? API_AUTO_REPORT_ID_ACCOUNT_TABLE_SAVE
                    : API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_SAVE);

                yield call(requestSaga, url, 'POST', { removeIds });
                yield put(loadTableMetaAction({ tableName }));
                break;
            case TableNameType.autoReportIdAccountEdit:
            case TableNameType.autoReportIdSubscriberEdit:

                url = yield call(autoReportGetApiUrl, tableName === TableNameType.autoReportIdAccountEdit
                    ? API_AUTO_REPORT_ID_ACCOUNT_TABLE_EDIT_SAVE
                    : API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_EDIT_SAVE);

                yield call(requestSaga, url, 'POST');

                const id = yield select(makeSelectorAutoReportCurrentId());
                openRoute(getPageAutoReportIdTableUrl(id, tableName));
                break;
        }
    } catch (e) {
        yield call(autoReportIdShowModalErrorSaga);
    }
}

export function* autoReportIdSaga() {
    yield all([
        takeEvery(AUTOREPORT_ID_GET_META_START, autoReportIdGetMetaSaga),
        takeEvery(AUTOREPORT_ID_GET_TAB_META_START, autoReportIdGetTabMetaSaga),
        takeEvery(AUTOREPORT_ID_UPDATE_FORMAT, autoReportIdSaveFormatSaga),
        takeEvery(AUTOREPORT_ID_UPDATE_SCHEDULE, autoReportIdUpdateScheduleSaga),
        takeEvery(AUTOREPORT_ID_SAVE_TABLE, autoReportIdSaveTableSaga),
    ]);
}
